import { 
  Component, 
  Input, 
  Output, 
  EventEmitter, 
  OnInit, 
  OnDestroy 
} from '@angular/core';
import { ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImageMetadata, LogoKind } from './model/image-metadata.model';
import { ImageUploadService } from './service/image-upload.service';

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.css']
})
export class ImageUploadComponent implements OnInit, OnDestroy {
  // Configuration inputs
  @Input() imageWidth: number = 400;
  @Input() imageHeight?: number;
  @Input() aspectRatio: string = 'free';
  @Input() acceptedFileTypes: string[] = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  @Input() outputFormat: string = 'png';
  @Input() label: string = 'Upload Image';
  @Input() helpText?: string;
  @Input() required: boolean = false;
  @Input() organizationId?: string;
  @Input() projectId?: string;
  @Input() customerId?: string;
  @Input() context: 'form' | 'org' | 'profile' = 'form';
  @Input() maxFileSize: number = 5242880; // 5MB default
  
  private _existingImageUrl?: string;
  @Input() set existingImageUrl(url: string | undefined) {
    this._existingImageUrl = url;
    // When existingImageUrl changes, update the imageUrl display
    if (url) {
      this.imageUrl = url;
    }
  }
  get existingImageUrl(): string | undefined {
    return this._existingImageUrl;
  }

  // Output events
  @Output() imageUploaded = new EventEmitter<ImageMetadata>();
  @Output() imageDeleted = new EventEmitter<string>();
  @Output() onImageCropped = new EventEmitter<Blob>();

  // Component state
  selectedFile: File | null = null;
  selectedFileName: string = '';
  imageChangedEvent: any = null;
  croppedImage: string | Blob = '';
  croppedImageBase64: string = '';
  isCropperReady: boolean = false;
  uploading: boolean = false;
  uploadProgress: number = 0;
  error: string | null = null;
  imageUrl: string | null = null;
  uploadedImageMetadata: ImageMetadata | null = null;

  private destroy$ = new Subject<void>();
  private uid: string;

  constructor(
    private imageUploadService: ImageUploadService
  ) {
    this.uid = this.generateUUID();
  }

  ngOnInit(): void {
    // Load existing image if provided
    if (this.existingImageUrl) {
      this.imageUrl = this.existingImageUrl;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle file selection from input
   */
  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.processFile(target.files[0]);
    }
  }

  /**
   * Handle file change event (for image cropper)
   */
  fileChangeEvent(event: any): void {
    this.imageChangedEvent = event;
    if (event.target.files && event.target.files.length > 0) {
      this.selectedFile = event.target.files[0];
      this.selectedFileName = this.selectedFile.name;
      this.validateFile(this.selectedFile);
    }
  }

  /**
   * Handle drag over event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Handle drop event
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.processFile(file);
      
      // Create a synthetic event for the image cropper
      const syntheticEvent = {
        target: {
          files: event.dataTransfer.files
        }
      };
      this.fileChangeEvent(syntheticEvent);
    }
  }

  /**
   * Process a selected file
   */
  private processFile(file: File): void {
    this.selectedFile = file;
    this.selectedFileName = file.name;
    this.error = null;
    
    if (!this.validateFile(file)) {
      this.selectedFile = null;
      this.selectedFileName = '';
      return;
    }
  }

  /**
   * Validate the selected file
   */
  private validateFile(file: File): boolean {
    // Validate file type
    if (!this.imageUploadService.validateFileType(file, this.acceptedFileTypes)) {
      this.error = `Invalid file type. Accepted types: ${this.acceptedFileTypes.join(', ')}`;
      return false;
    }

    // Validate file size
    if (!this.imageUploadService.validateFileSize(file, this.maxFileSize)) {
      const maxSizeMB = (this.maxFileSize / 1048576).toFixed(2);
      this.error = `File size exceeds maximum limit of ${maxSizeMB}MB`;
      return false;
    }

    return true;
  }

  /**
   * Handle image cropped event from ngx-image-cropper
   */
  imageCropped(event: ImageCroppedEvent): void {
    if (event.base64) {
      this.croppedImageBase64 = event.base64;
      this.croppedImage = event.base64;
      
      // Convert base64 to Blob for the event emitter
      fetch(event.base64)
        .then(res => res.blob())
        .then(blob => this.onImageCropped.emit(blob));
    }
  }

  /**
   * Handle cropper ready event
   */
  onCropperReady(): void {
    this.isCropperReady = true;
  }

  /**
   * Handle image load failed event
   */
  loadImageFailed(): void {
    this.error = 'Failed to load image. Please try a different file.';
    this.resetCropper();
  }

  /**
   * Convert image format asynchronously using canvas
   * Waits for image to load before conversion
   */
  private convertImageFormatAsync(base64String: string, outputFormat: string): Promise<string> {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            console.warn('Could not get canvas context, returning original');
            resolve(base64String);
            return;
          }
          
          ctx.drawImage(img, 0, 0);
          
          // Map output format to canvas toDataURL format
          const mimeTypeMap: { [key: string]: string } = {
            'png': 'image/png',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'gif': 'image/gif',
            'webp': 'image/webp',
            'bmp': 'image/bmp'
          };
          
          const mimeType = mimeTypeMap[outputFormat.toLowerCase()] || 'image/png';
          const quality = mimeType === 'image/jpeg' ? 0.9 : undefined;
          
          const converted = canvas.toDataURL(mimeType, quality);
          resolve(converted);
        };
        
        img.onerror = () => {
          console.warn('Error loading image for format conversion, returning original');
          resolve(base64String);
        };
        
        img.src = base64String;
      } catch (error) {
        console.warn('Error converting image format:', error);
        resolve(base64String);
      }
    });
  }

  /**
   * Get the computed aspect ratio for the cropper
   */
  getAspectRatio(): number {
    const ratioMap: { [key: string]: number } = {
      'free': 0, // 0 means free aspect ratio in ngx-image-cropper
      '1:1': 1,
      '4:3': 4/3,
      '16:9': 16/9,
      '16:10': 16/10,
      '4:1': 4/1,
    };

    if (this.aspectRatio in ratioMap) {
      return ratioMap[this.aspectRatio];
    }

    // Handle custom ratio like "width:height"
    if (this.aspectRatio.includes(':')) {
      const [width, height] = this.aspectRatio.split(':').map(Number);
      return width / height;
    }

    // If imageWidth and imageHeight are provided, calculate ratio
    if (this.imageWidth && this.imageHeight) {
      return this.imageWidth / this.imageHeight;
    }

    return 0; // Free aspect ratio
  }

  /**
   * Upload the cropped image to S3
   */
  async uploadImage(): Promise<void> {
    if (!this.croppedImageBase64 || !this.selectedFileName) {
      this.error = 'No image to upload';
      return;
    }

    this.uploading = true;
    this.error = null;
    this.uploadProgress = 0;

    try {
      // Generate filename with organization/project context
      const timestamp = new Date().getTime();
      const contextPrefix = this.getContextPrefix();
      
      // Apply output format to filename if specified
      let baseFilename = this.selectedFileName;
      if (this.outputFormat && this.outputFormat !== 'image') {
        // Remove original extension and add the configured output format
        baseFilename = baseFilename.replace(/\.[^/.]+$/, '') + `.${this.outputFormat.toLowerCase()}`;
      }
      
      const filename = `${contextPrefix}_${timestamp}_${baseFilename}`;

      // Convert format if output format specified
      let base64ToUpload = this.croppedImageBase64;
      if (this.outputFormat && this.outputFormat !== 'image') {
        base64ToUpload = await this.convertImageFormatAsync(this.croppedImageBase64, this.outputFormat);
      }

      // Convert base64 to File
      const file = this.imageUploadService.base64ToFile(base64ToUpload, filename);

      // Get signed upload URL
      this.uploadProgress = 20;
      const signedUrlResponse = await this.imageUploadService
        .getSignedUploadUrl(filename, this.getLogoKind(), this.organizationId)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      if (!signedUrlResponse?.url) {
        throw new Error('Failed to get signed upload URL');
      }

      // Upload to S3
      this.uploadProgress = 40;
      await this.imageUploadService
        .uploadImageToS3(file, signedUrlResponse.url)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      this.uploadProgress = 70;

      // Prepare metadata
      // Determine the MIME type based on output format
      let mimeType = file.type; // Default to original file type
      if (this.outputFormat) {
        const mimeTypeMap: { [key: string]: string } = {
          'png': 'image/png',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'gif': 'image/gif',
          'webp': 'image/webp',
          'bmp': 'image/bmp'
        };
        mimeType = mimeTypeMap[this.outputFormat.toLowerCase()] || file.type;
      }
      
      const metadata: ImageMetadata = {
        filename: signedUrlResponse.key,
        originalWidth: this.imageWidth,
        originalHeight: this.imageHeight || this.imageWidth,
        croppedWidth: this.imageWidth,
        croppedHeight: this.imageHeight || this.imageWidth,
        aspectRatio: this.aspectRatio,
        uploadDate: new Date(),
        uploadedBy: '', // TODO: Get from auth service
        organizationId: this.organizationId,
        projectId: this.projectId,
        customerId: this.customerId,
        context: this.context,
        mimeType: mimeType,
        fileSize: file.size,
        imageUrl: signedUrlResponse.objectUrl
      };

      // Save metadata to database
      const imgType = this.aspectRatio || this.context || 'form'; // Use aspect ratio as img type, fallback to context
      const savedMetadata = await this.imageUploadService
        .saveImageMetadata(metadata, this.context, imgType)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      this.uploadProgress = 100;

      // Cache the image URL
      if (this.organizationId) {
        const cacheKey = `currentOrgLogoUrl-${this.organizationId}_${this.aspectRatio}`;
        sessionStorage.setItem(cacheKey, metadata.imageUrl);
      }

      // Update component state
      this.imageUrl = metadata.imageUrl;
      // Use local metadata if backend response is empty or doesn't have required fields
      this.uploadedImageMetadata = (savedMetadata && savedMetadata.imageUrl) ? savedMetadata : metadata;
      
      console.log('[ImageUploadComponent] uploadImage - uploadedImageMetadata being emitted:', this.uploadedImageMetadata);

      // Emit success event
      this.imageUploaded.emit(this.uploadedImageMetadata);

      // Reset cropper
      this.resetCropper();

    } catch (error) {
      console.error('Image upload failed:', error);
      this.error = 'Failed to upload image. Please try again.';
    } finally {
      this.uploading = false;
      this.uploadProgress = 0;
    }
  }

  /**
   * Reset the cropper and clear selection
   */
  resetCropper(): void {
    this.isCropperReady = false;
    this.selectedFile = null;
    this.selectedFileName = '';
    this.imageChangedEvent = null;
    this.croppedImage = '';
    this.croppedImageBase64 = '';
    this.error = null;
  }

  /**
   * Delete the uploaded image
   */
  async deleteImage(): Promise<void> {
    if (!this.uploadedImageMetadata?.id && !this.imageUrl) {
      return;
    }

    // Show confirmation dialog
    if (!confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      if (this.uploadedImageMetadata?.id) {
        await this.imageUploadService
          .deleteImage(this.uploadedImageMetadata.id)
          .pipe(takeUntil(this.destroy$))
          .toPromise();
      }

      // Clear cache
      if (this.organizationId) {
        const cacheKey = `currentOrgLogoUrl-${this.organizationId}_${this.aspectRatio}`;
        sessionStorage.removeItem(cacheKey);
      }

      // Clear component state
      this.imageUrl = null;
      this.uploadedImageMetadata = null;

      // Emit delete event
      this.imageDeleted.emit(this.uploadedImageMetadata?.id || '');

    } catch (error) {
      console.error('Image deletion failed:', error);
      this.error = 'Failed to delete image. Please try again.';
    }
  }

  /**
   * Download the current image
   */
  downloadImage(): void {
    if (!this.imageUrl) {
      return;
    }

    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = this.imageUrl;
      link.target = '_blank';
      
      // Try to get filename from metadata or generate one
      let filename = this.uploadedImageMetadata?.filename || 'image';
      if (!filename.includes('.')) {
        const ext = this.outputFormat || 'png';
        filename += `.${ext}`;
      }
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Image download failed:', error);
      this.error = 'Failed to download image. Please try again.';
    }
  }

  /**
   * Allow user to upload a new image, replacing the current one
   */
  uploadNewImage(): void {
    // Reset the cropper state to allow new upload
    this.resetCropper();
    // Trigger file input click
    const selector = 'input[type="file"]';
    const fileUploadElement = document.querySelector(selector) as HTMLInputElement;
    if (fileUploadElement) {
      fileUploadElement.click();
    }
  }

  /**
   * Get context prefix for filename
   */
  private getLogoKind(): LogoKind {
    if (this.context === 'org') return 'org';
    if (this.context === 'profile') return 'person';
    return 'image';
  }

  private getContextPrefix(): string {
    if (this.organizationId) return `org_${this.organizationId}`;
    if (this.projectId) return `project_${this.projectId}`;
    if (this.customerId) return `customer_${this.customerId}`;
    return this.context;
  }

  /**
   * Generate a UUID for unique component identification
   */
  private generateUUID(): string {
    let uuidValue = '', k, randomValue;
    for (k = 0; k < 32; k++) {
      randomValue = Math.random() * 16 | 0;
      if (k === 8 || k === 12 || k === 16 || k === 20) {
        uuidValue += '-';
      }
      uuidValue += (k === 12 ? 4 : (k === 16 ? (randomValue & 3 | 8) : randomValue)).toString(16);
    }
    return uuidValue;
  }

  /**
   * Check if maintainAspectRatio should be true
   */
  shouldMaintainAspectRatio(): boolean {
    return this.aspectRatio !== 'free';
  }

  /**
   * Get formatted file types string for display
   */
  getFormattedFileTypes(): string {
    return this.acceptedFileTypes
      .map(type => type.replace(/image\//g, ''))
      .join(', ')
      .toUpperCase();
  }

  /**
   * Get formatted max file size for display
   */
  getFormattedMaxFileSize(): string {
    return (this.maxFileSize / 1048576).toFixed(2);
  }

  /**
   * Get accept attribute value for file input
   */
  getAcceptAttribute(): string {
    return this.acceptedFileTypes.join(',');
  }

  /**
   * Get formatted upload progress percentage
   */
  getUploadProgressPercent(): string {
    return `${this.uploadProgress}%`;
  }

  /**
   * Get progress bar width style
   */
  getProgressBarWidth(): string {
    return `${this.uploadProgress}%`;
  }
}
